import React, { useEffect, useState } from 'react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';
import { brandColors } from '../config/brandColors';
import { definitions } from './MaturityDefinitions';

interface MaturityData {
  dimension: string;
  score: number;
  maxScore: number;
  percentage: number;
  questionCount: number;
  answeredCount: number;
}

interface MaturitySpiderChartProps {
  data: MaturityData[];
  title?: string;
  showLegend?: boolean;
  size?: 'small' | 'medium' | 'large';
  showPoints?: boolean; // If false, hide point details (for client view)
}

const progressionStages = [
  { label: 'Initial', color: brandColors.secondary[300] },
  { label: 'Managed', color: brandColors.secondary[400] },
  { label: '(Re)Defined', color: brandColors.secondary[600] },
  { label: 'Measured', color: brandColors.secondary[800] },
  { label: 'Optimizing', color: brandColors.secondary[900] },
];

const MaturitySpiderChart: React.FC<MaturitySpiderChartProps> = ({
  data,
  title = "Maturity Evaluation",
  showLegend = true,
  size = 'medium',
  showPoints = true
}) => {
  const [tooltip, setTooltip] = useState<{ x: number; y: number; title: string; description: string } | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const updateViewport = () => {
      setIsMobile(window.innerWidth < 640);
    };

    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  // Refs for tooltip timers to manage timeouts across renders/events
  const hideTimeoutRef = React.useRef<number | null>(null);
  const maxDurationTimeoutRef = React.useRef<number | null>(null);

  // Clear all pending timeouts
  const clearTimers = () => {
    if (hideTimeoutRef.current) {
      window.clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    if (maxDurationTimeoutRef.current) {
      window.clearTimeout(maxDurationTimeoutRef.current);
      maxDurationTimeoutRef.current = null;
    }
  };

  // Transform data for the radar chart
  const chartData = data.map(item => ({
    dimension: item.dimension,
    score: item.percentage, // Use percentage for the chart (0-100)
    fullMark: 100 // Maximum possible score
  }));

  // Get maturity stage label and color
  const getMaturityStage = (percentage: number) => {
    if (percentage <= 20) return { label: "Initial", color: brandColors.secondary[300] };
    if (percentage <= 40) return { label: "Managed", color: brandColors.secondary[400] };
    if (percentage <= 60) return { label: "(Re)Defined", color: brandColors.secondary[600] };
    if (percentage <= 80) return { label: "Measured", color: brandColors.secondary[800] };
    return { label: "Optimizing", color: brandColors.secondary[900] };
  };

  // Size configurations
  const sizeConfig = {
    small: { height: 320, fontSize: 11, labelPadding: 12, margin: { top: 24, right: 32, bottom: 24, left: 32 } },
    medium: { height: 400, fontSize: 14, labelPadding: 20, margin: { top: 40, right: 70, bottom: 40, left: 70 } },
    large: { height: 500, fontSize: 16, labelPadding: 20, margin: { top: 40, right: 80, bottom: 40, left: 80 } }
  };

  const effectiveSize = isMobile ? 'small' : size;
  const config = sizeConfig[effectiveSize];

  const getTickLines = (label: string): string[] => {
    if (!isMobile) return [label];

    const words = label.split(' ').filter(Boolean);
    if (words.length <= 1) return [label];

    const lines: string[] = [];
    let currentLine = words[0];

    for (let i = 1; i < words.length; i += 1) {
      const candidate = `${currentLine} ${words[i]}`;
      if (candidate.length <= 14) {
        currentLine = candidate;
      } else {
        lines.push(currentLine);
        currentLine = words[i];
      }
    }

    lines.push(currentLine);
    return lines;
  };

  // Custom Tick Component for Tooltips
  const CustomTick = (props: any) => {
    const { payload, x, y, cx, cy } = props;

    // Improved positioning logic:
    // Move slightly further out from the center to avoid overlapping the chart
    // Calculate vector from center (cx, cy) to point (x, y)
    const dx = x - cx;
    const dy = y - cy;
    // Normalize and scale up slightly
    const length = Math.sqrt(dx * dx + dy * dy);
    // Add extra padding so labels sit outside the plot area.
    const padding = config.labelPadding;
    const finalX = length > 0 ? cx + (dx / length) * (length + padding) : x;
    const finalY = length > 0 ? cy + (dy / length) * (length + padding) : y;
    const labelLines = getTickLines(payload.value || '');
    const lineHeight = config.fontSize + (isMobile ? 1 : 2);
    const firstDy = labelLines.length > 1 ? -((labelLines.length - 1) * lineHeight) / 2 : 4;

    return (
      <g transform={`translate(${finalX},${finalY})`}>
        <text
          x={0}
          y={0}
          dy={4}
          textAnchor={x > cx ? 'start' : x < cx ? 'end' : 'middle'}
          fill="#374151"
          fontSize={config.fontSize}
          className="cursor-help font-semibold hover:fill-indigo-600 transition-colors duration-200"
          onMouseEnter={(e) => {
            clearTimers(); // Clear any existing leave or max timers

            const def = definitions.find(d => d.name.toUpperCase() === payload.value.toUpperCase());
            if (def) {
              // Get bounding rect for precise positioning relative to the viewport/container
              const rect = e.currentTarget.getBoundingClientRect();
              setTooltip({
                x: rect.left + rect.width / 2, // Center of label
                y: rect.top, // Top of label
                title: payload.value,
                description: def.description
              });

              // Set max duration timer (7 seconds)
              maxDurationTimeoutRef.current = window.setTimeout(() => {
                setTooltip(null);
                maxDurationTimeoutRef.current = null;
              }, 7000);
            }
          }}
          onMouseLeave={() => {
            // When leaving, set a delay before hiding (2 seconds)
            clearTimers(); // Clear the max duration timer as we are entering exit phase // Actually, wait. 
            // If we clear max timer, we just rely on hide timer. Correct.

            hideTimeoutRef.current = window.setTimeout(() => {
              setTooltip(null);
              hideTimeoutRef.current = null;
            }, 2000);
          }}
        >
          {labelLines.map((line, index) => (
            <tspan
              key={`${payload.value}-${index}`}
              x={0}
              dy={index === 0 ? firstDy : lineHeight}
            >
              {line}
            </tspan>
          ))}
        </text>
      </g>
    );
  };

  return (
    <div className="w-full relative">
      {/* Absolute Positioned Tooltip */}
      {tooltip && (
        <div
          className="fixed z-50 w-64 bg-white p-3 rounded-xl shadow-xl border border-indigo-100 pointer-events-none transform -translate-x-1/2 -translate-y-full"
          style={{
            left: tooltip.x,
            top: tooltip.y - 10, // 10px spacing above the label
            animation: 'fadeIn 0.2s ease-out'
          }}
        >
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-3 h-3 bg-white border-b border-r border-indigo-100"></div>
          <h4 className="font-bold text-gray-900 mb-1 text-sm uppercase tracking-wide border-b border-gray-100 pb-1">
            {tooltip.title}
          </h4>
          <p className="text-xs text-gray-600 leading-relaxed font-medium">
            {tooltip.description}
          </p>
        </div>
      )}

      {title && (
        <h3 className="text-lg font-semibold text-neutral-900 mb-4 text-center">
          {title}
        </h3>
      )}

      <div className="bg-white p-3 sm:p-4 rounded-lg border border-neutral-200 overflow-visible">
        <ResponsiveContainer width="100%" height={config.height}>
          <RadarChart data={chartData} margin={config.margin}>
            <PolarGrid stroke="#E5E7EB" />
            <PolarAngleAxis
              dataKey="dimension"
              tick={<CustomTick />}
            />
            <PolarRadiusAxis
              angle={90}
              domain={[0, 100]}
              tick={false}
            />
            <Radar
              name="Maturity Score"
              dataKey="score"
              stroke={brandColors.secondary[600]}
              strokeWidth={2}
              fill={brandColors.secondary[600]}
              fillOpacity={0.6}
            />
          </RadarChart>
        </ResponsiveContainer>

        {showLegend && (
          <div className="mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-sm auto-rows-fr">
              {data.map((item) => {
                const stage = getMaturityStage(item.percentage);
                return (
                  <div
                    key={item.dimension}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 px-3 py-3 rounded-lg shadow-sm h-full"
                    style={{ backgroundColor: stage.color }}
                  >
                    <span className="text-white font-medium break-words pr-2">{item.dimension}</span>
                    <span className="text-white font-bold whitespace-nowrap">
                      {stage.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Maturity Progression Indicator */}
        <div className="border-t border-gray-100 pt-6 mt-6">
          <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Maturity Progression
          </h4>
          <div className="hidden md:flex w-full items-stretch filter drop-shadow-sm mt-4 min-h-[3rem] md:min-h-[3.5rem]">
            {progressionStages.map((stage, index) => {
              const isFirst = index === 0;
              return (
                <div
                  key={stage.label}
                  className="flex-1 flex items-center justify-center relative transition-transform hover:scale-[1.02] duration-200"
                  style={{
                    backgroundColor: stage.color,
                    marginLeft: isFirst ? 0 : '-1.5rem', // Overlap
                    clipPath: isFirst
                      ? 'polygon(0% 0%, calc(100% - 1.5rem) 0%, 100% 50%, calc(100% - 1.5rem) 100%, 0% 100%)'
                      : 'polygon(0% 0%, calc(100% - 1.5rem) 0%, 100% 50%, calc(100% - 1.5rem) 100%, 0% 100%, 1.5rem 50%)',
                    zIndex: index
                  }}
                >
                  <span className="font-bold text-white text-xs md:text-sm lg:text-base whitespace-nowrap px-4 md:pl-8 text-center leading-tight">
                    {stage.label}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="md:hidden grid grid-cols-1 gap-2 mt-4">
            {progressionStages.map((stage) => (
              <div
                key={stage.label}
                className="w-full rounded-lg px-3 py-2 text-center"
                style={{ backgroundColor: stage.color }}
              >
                <span className="font-semibold text-white text-sm">
                  {stage.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Detailed scores table */}
      <div className="mt-4 bg-neutral-50 p-4 rounded-lg">
        <h4 className="text-sm font-medium text-neutral-900 mb-3">Detailed Scores</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.map((item) => {
            const def = definitions.find(d => d.name.toUpperCase() === item.dimension.toUpperCase());
            const description = def ? def.description : '';
            const stage = getMaturityStage(item.percentage);

            return (
              <div key={item.dimension} className="bg-white p-3 rounded border">
                <div className="flex justify-between items-start gap-2 mb-2">
                  <span
                    className="text-sm font-medium text-neutral-900 break-words min-w-0 cursor-help hover:text-indigo-600 transition-colors duration-200"
                    onMouseEnter={(e) => {
                      clearTimers();
                      const def = definitions.find(d => d.name.toUpperCase() === item.dimension.toUpperCase());
                      if (def) {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setTooltip({
                          x: rect.left + rect.width / 2,
                          y: rect.top,
                          title: item.dimension,
                          description: def.description
                        });
                        maxDurationTimeoutRef.current = window.setTimeout(() => {
                          setTooltip(null);
                          maxDurationTimeoutRef.current = null;
                        }, 7000);
                      }
                    }}
                    onMouseLeave={() => {
                      clearTimers();
                      hideTimeoutRef.current = window.setTimeout(() => {
                        setTooltip(null);
                        hideTimeoutRef.current = null;
                      }, 2000);
                    }}
                  >
                    {item.dimension}
                  </span>
                  <span className="text-sm font-bold text-neutral-700 shrink-0">
                    {showPoints ? `${item.percentage.toFixed(0)}%` : stage.label}
                  </span>
                </div>
                <div className="w-full bg-neutral-200 rounded-full h-2 relative overflow-hidden">
                  {/* Faded bar for completion (Visual indication of effort) when showPoints is false */}
                  {!showPoints && (
                    <div
                      className="absolute top-0 left-0 h-full bg-neutral-300 transition-all duration-300"
                      style={{
                        width: `${item.questionCount > 0 ? (item.answeredCount / item.questionCount) * 100 : 0}%`
                      }}
                    />
                  )}
                  <div
                    className={`h-full rounded-full transition-all duration-300 absolute top-0 left-0 ${!showPoints ? 'opacity-80' : ''}`}
                    style={{
                      width: `${item.percentage}%`,
                      backgroundColor: stage.color,
                      zIndex: 10
                    }}
                  ></div>
                </div>
                {showPoints && (
                  <div className="text-xs text-neutral-500 mt-1">
                    {Math.round(item.score)} / {Math.round(item.maxScore)} points
                  </div>
                )}
                {item.answeredCount !== undefined && item.questionCount !== undefined && (
                  <div className="flex justify-between items-center text-[11px] text-neutral-500 mt-1.5 border-t border-neutral-100 pt-1.5">
                    <span>{item.answeredCount} of {item.questionCount} answered</span>
                    <span>
                      {item.questionCount > 0 ? Math.round((item.answeredCount / item.questionCount) * 100) : 0}% complete
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MaturitySpiderChart;
